// ============================================================================
// APPOINTMENT WARNING SYSTEM
// ============================================================================
// Sistema escalable de warnings para citas del calendario
// ============================================================================

export type WarningSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface IAppointmentWarning {
  id: string;
  severity: WarningSeverity;
  icon: string;
  labelKey: string; // Translation key
  label?: string; // Resolved translation
  actionKey?: string; // Optional action translation key
  action?: string; // Resolved action translation
}

// Definición de todos los warnings posibles
export const APPOINTMENT_WARNINGS: Record<
  string,
  Omit<IAppointmentWarning, 'label' | 'action'>
> = {
  NOT_PAID: {
    id: 'NOT_PAID',
    severity: 'high',
    icon: 'fas fa-euro-sign',
    labelKey: 'WARNINGS.NOT_PAID',
    actionKey: 'WARNINGS.ACTION_PAY',
  },
  NO_TREATMENT: {
    id: 'NO_TREATMENT',
    severity: 'critical',
    icon: 'fas fa-briefcase-medical',
    labelKey: 'WARNINGS.NO_TREATMENT',
    actionKey: 'WARNINGS.ACTION_ASSIGN_TREATMENT',
  },
  NO_PATIENT: {
    id: 'NO_PATIENT',
    severity: 'critical',
    icon: 'fas fa-user-slash',
    labelKey: 'WARNINGS.NO_PATIENT',
    actionKey: 'WARNINGS.ACTION_ASSIGN_PATIENT',
  },
  PATIENT_INCOMPLETE: {
    id: 'PATIENT_INCOMPLETE',
    severity: 'medium',
    icon: 'fas fa-user-edit',
    labelKey: 'WARNINGS.PATIENT_INCOMPLETE',
    actionKey: 'WARNINGS.ACTION_COMPLETE_PATIENT',
  },
  NO_PROFESSIONAL: {
    id: 'NO_PROFESSIONAL',
    severity: 'critical',
    icon: 'fas fa-user-md',
    labelKey: 'WARNINGS.NO_PROFESSIONAL',
    actionKey: 'WARNINGS.ACTION_ASSIGN_PROFESSIONAL',
  },
  REQUIRES_CONFIRMATION: {
    id: 'REQUIRES_CONFIRMATION',
    severity: 'medium',
    icon: 'fas fa-phone',
    labelKey: 'WARNINGS.REQUIRES_CONFIRMATION',
    actionKey: 'WARNINGS.ACTION_CONFIRM',
  },
  FIRST_VISIT: {
    id: 'FIRST_VISIT',
    severity: 'low',
    icon: 'fas fa-star',
    labelKey: 'WARNINGS.FIRST_VISIT',
  },
};

// Orden de severidad (mayor número = más severo)
export const SEVERITY_ORDER: Record<WarningSeverity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// Helper para obtener la severidad máxima de una lista de warnings
export function getMaxSeverity(
  warnings: IAppointmentWarning[],
): WarningSeverity | null {
  if (!warnings || warnings.length === 0) return null;

  return warnings.reduce((max, warning) => {
    return SEVERITY_ORDER[warning.severity] > SEVERITY_ORDER[max.severity]
      ? warning
      : max;
  }).severity;
}

// Helper para ordenar warnings por severidad (más severos primero)
export function sortWarningsBySeverity(
  warnings: IAppointmentWarning[],
): IAppointmentWarning[] {
  return [...warnings].sort(
    (a, b) => SEVERITY_ORDER[b.severity] - SEVERITY_ORDER[a.severity],
  );
}
